import React, { useState, useEffect } from 'react';
import { notification, Spin, Modal, Row } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import defaultUserImage from "../../assets/images/Drawer_Image.svg";
import { getPresignedUrlOfUserProfile, getImageUrlOfUserProfile } from '../../api/User.js';
import { componentStyles } from "./ProfileStyles.js";

const ProfileImageUpload = () => {
    const [isHovered, setIsHovered] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalImageHovered, setIsModalImageHovered] = useState(false);

    const fetchImageUrl = async () => {
        try {
            const response = await getImageUrlOfUserProfile();
            if (response && response.profileImageUrl) {
                setSelectedImage(response.profileImageUrl);
            }
        } catch (error) {
            console.error("Error fetching profile image URL:", error);
            notification.error({
                message: 'Fetch Failed',
                description: 'There was an error fetching your profile image. Please try again.',
                placement: 'topRight',
            });
        }
    };

    useEffect(() => {
        fetchImageUrl();
    }, []);

    const uploadImageToS3 = async (file) => {
        try {
            const fileType = file.type;
            const response = await getPresignedUrlOfUserProfile({ fileType: fileType });
            const presignedUrl = response.uploadUrl;

            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': fileType,
                },
                body: file,
            });

            if (uploadResponse.ok) {
                const imageUrl = presignedUrl.split('?')[0];
                return imageUrl;
            } else {
                throw new Error("Upload to S3 failed");
            }
        } catch (error) {
            console.error("Error uploading file: ", error);
            throw error;
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['jpeg', 'jpg', 'png'];
        const fileType = file.type.split('/')[1];

        if (!allowedTypes.includes(fileType)) {
            notification.error({
                message: 'Invalid file type',
                description: 'Please upload a JPEG or PNG image.',
                placement: 'topRight'
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage(reader.result);
        };
        reader.readAsDataURL(file);

        try {
            setIsUploading(true);
            const imageUrl = await uploadImageToS3(file);
            
            notification.success({
                message: 'Upload Successful',
                description: 'Your profile image has been updated.',
                placement: 'topRight'
            });

            setSelectedImage(imageUrl);
            window.location.reload();
        } catch (error) {
            console.error('Upload error:', error);
            notification.error({
                message: 'Upload Failed',
                description: 'There was an error uploading your image. Please try again.',
                placement: 'topRight'
            });
            setSelectedImage(null);
        } finally {
            setIsUploading(false);
            setIsModalVisible(false);
        }
    };

    const containerStyle = {
        ...componentStyles.imageContainer,
        backgroundColor: isHovered ? '#f0f9ff' : '#e6f7ff',
        border: isHovered ? '1px solid #91d5ff' : '1px solid transparent',
        opacity: isUploading ? 0.7 : 1,
    };

    const inputStyle = {
        ...componentStyles.fileInput,
        cursor: isUploading ? 'not-allowed' : 'pointer',
    };

    const defaultImageStyle = {
        ...componentStyles.defaultImage,
        opacity: isHovered ? 0.3 : 1,
    };

    const modalImageContainerStyle = {
        position: 'relative',
        margin: '0 auto',
        borderRadius: '50%',
        overflow: 'hidden',
        width: "300px",
        height: "300px",
    };

    const modalImageStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    };

    const modalUploadOverlay = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
        opacity: isModalImageHovered ? 1 : 0,
        transition: 'opacity 0.3s',
        borderRadius: '50%',
    };

    const modalUploadIcon = {
        fontSize: '32px',
        color: 'white',
    };

    const modalStyle = {
        top: '20%',
        padding: 0,
        width: '30% !important',
    };

    return (
        <>
            <div
                className="profile-upload-container"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={componentStyles.uploadContainer}
                onClick={() => setIsModalVisible(true)}
            >
                <div style={containerStyle}>
                    {selectedImage ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <img
                                src={selectedImage}
                                alt="Profile"
                                style={componentStyles.profileImage}
                            />
                            {isHovered && !isUploading && (
                                <div style={componentStyles.uploadOverlay}>
                                    <UploadOutlined style={componentStyles.uploadIcon} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <img
                                src={defaultUserImage}
                                alt="Default Profile"
                                style={defaultImageStyle}
                            />
                            <UploadOutlined
                                style={{
                                    ...componentStyles.uploadIcon,
                                    opacity: isHovered ? 1 : 0,
                                }}
                            />
                        </>
                    )}
                    {isUploading && (
                        <div style={componentStyles.spinnerOverlay}>
                            <Spin size="large" />
                        </div>
                    )}
                </div>
            </div>
        
            <Modal
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                closable={false}
                width={300}
                style={modalStyle}
                bodyStyle={{ padding: 0 }}
                maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
                className="profile-image-modal"
            >
                <div
                    style={modalImageContainerStyle}
                    onMouseEnter={() => setIsModalImageHovered(true)}
                    onMouseLeave={() => setIsModalImageHovered(false)}
                >
                    <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                        style={{
                            ...inputStyle,
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                        }}
                    />
                    <img
                        src={selectedImage || defaultUserImage}
                        alt="Profile"
                        style={modalImageStyle}
                    />
                    <div style={modalUploadOverlay}>
                        <UploadOutlined style={modalUploadIcon} />
                    </div>
                    {isUploading && (
                        <div style={componentStyles.spinnerOverlay}>
                            <Spin size="large" />
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ProfileImageUpload;